import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { clsx } from 'clsx';
import { Menu, X, Moon, Sun } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../ui/Button';

interface NavLink {
  href: string;
  label: string;
  external?: boolean;
}

const navLinks: NavLink[] = [
  { href: '/', label: 'Home' },
  { href: '/how-it-works', label: 'How It Works' },
  { href: 'https://app.churnlogix.com', label: 'Tool', external: true },
  { href: '/projects', label: 'Case Studies' },
  { href: '/newsletter', label: 'Newsletter' },
  { href: '/contact', label: 'Contact' },
];

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { actualTheme, toggleTheme } = useTheme();
  const { user, isAdmin } = useAuth();
  const location = useLocation();

  const isActive = (href: string) => {
    if (href.startsWith('http')) return false;
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <header className="sticky top-0 z-40 glass-effect">
      <nav className="container-custom" aria-label="Main navigation">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3"
          >
            <img
              src={actualTheme === 'dark' ? '/logo-icon-dark.svg' : '/logo-icon-light.svg'}
              alt="ChurnLogix"
              className="w-10 h-10"
            />
            <span className="text-xl font-heading font-black">
              <span className="text-obsidian dark:text-signal">CHURN</span>
              <span className="text-slate-400 dark:text-slate-500">LOGIX</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.external ? (
                <a
                  key={link.href}
                  href={link.href}
                  className="text-sm font-medium text-slate-600 hover:text-obsidian dark:text-slate-400 dark:hover:text-signal transition-colors duration-200"
                >
                  {link.label}
                </a>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className={clsx(
                    'text-sm font-medium transition-colors duration-200',
                    isActive(link.href)
                      ? 'text-amber'
                      : 'text-slate-600 hover:text-obsidian dark:text-slate-400 dark:hover:text-signal'
                  )}
                >
                  {link.label}
                </Link>
              )
            ))}
          </div>

          {/* Desktop Actions */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={toggleTheme}
              className="p-2 rounded text-slate-600 hover:bg-slate-100 hover:text-obsidian dark:text-slate-400 dark:hover:bg-obsidian-50 dark:hover:text-signal transition-colors"
              aria-label={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {actualTheme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>

            {/* Only show dashboard link if user is logged in */}
            {user && (
              <Link to="/dashboard">
                <Button variant="primary" size="sm">
                  {isAdmin ? 'Admin Dashboard' : 'Dashboard'}
                </Button>
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 rounded text-slate-600 hover:bg-slate-100 hover:text-obsidian dark:text-slate-400 dark:hover:bg-obsidian-50 dark:hover:text-signal transition-colors"
              aria-label={`Switch to ${actualTheme === 'dark' ? 'light' : 'dark'} mode`}
            >
              {actualTheme === 'dark' ? (
                <Sun className="w-5 h-5" />
              ) : (
                <Moon className="w-5 h-5" />
              )}
            </button>
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 rounded text-slate-600 hover:bg-slate-100 hover:text-obsidian dark:text-slate-400 dark:hover:bg-obsidian-50 dark:hover:text-signal transition-colors"
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200 dark:border-slate-800 animate-slide-down">
            <div className="flex flex-col gap-2">
              {navLinks.map((link) => (
                link.external ? (
                  <a
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-2 rounded text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-obsidian dark:text-slate-400 dark:hover:bg-obsidian-50 dark:hover:text-signal transition-colors duration-200"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    key={link.href}
                    to={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className={clsx(
                      'px-4 py-2 rounded text-sm font-medium transition-colors duration-200',
                      isActive(link.href)
                        ? 'bg-amber/10 text-amber'
                        : 'text-slate-600 hover:bg-slate-100 hover:text-obsidian dark:text-slate-400 dark:hover:bg-obsidian-50 dark:hover:text-signal'
                    )}
                  >
                    {link.label}
                  </Link>
                )
              ))}
              {user && (
                <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-800">
                  <Link
                    to="/dashboard"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block"
                  >
                    <Button variant="primary" size="sm" className="w-full">
                      {isAdmin ? 'Admin Dashboard' : 'Dashboard'}
                    </Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </nav>
    </header>
  );
}

export default Header;
